import { HashCardDisplay } from '@/libs/components/src/lib/HashCardDisplay';
import { Icons } from '@/libs/components/src/lib/icons';
import { TimestampDisplay } from '@/libs/components/src/lib/TimestampDisplay';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { Checkbox } from '@chiroup/components/form/Checkbox';
import { Input } from '@chiroup/components/form/Input';
import { Select } from '@chiroup/components/form/Select';
import { Icon } from '@chiroup/components/Icon';
import { Icons as SharedIcons } from '@chiroup/components/icons';
import { ColumnDef, DataTable } from '@chiroup/components/ui/data-table';
import { ChiroUpDayJsCommon } from '@chiroup/core/constants/stringConstants';
import { MILLISECONDS } from '@chiroup/core/constants/time';
import { ChiroUpJSON } from '@chiroup/core/functions/ChiroUpJSON';
import {
  ConnectorConsoleExchange,
  ConnectorDrainRunSummary,
  ConnectorExecution,
  ConnectorRunLockInfo,
  ConnectorVendorStatus,
} from '@chiroup/core/types/ConnectorAdmin.type';
import {
  ConnectorTraceDirection,
  ConnectorTraceRow,
} from '@chiroup/core/types/ConnectorTrace.type';
import {
  ConnectorManualRun,
  ConnectorManualRunKickResult,
} from '@chiroup/core/types/Integration.type';
import { IntegrationDocumentType } from '@chiroup/core/types/IntegrationConnector.type';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { CircleHelp as QuestionMarkCircleIcon } from 'lucide-react';
import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ToastContext, ToastTypes } from '../../contexts/toast.context';
import {
  useClearTransmitLock,
  useConnectionApiConsole,
  useConnectorExchange,
  useInvalidateConsole,
  usePromoteCandidate,
  usePurgeTransmitQueue,
  useRetryExchange,
  useReviewActivity,
  useRollbackCredentials,
  useRunCredentialCheck,
  useRunInboundDrain,
  useRunTransmitBackstop,
  useSetTraceConfig,
  useStageCandidate,
  useTogglePolling,
  useValidateCandidate,
} from '../../hooks/useConnectionApi';
import connectionApiService from '../../services/connectionApi.service';
import Modal from '../common/Modal';
import { connectorErrorMessage } from './connectorErrorMessage';

dayjs.extend(utc);
dayjs.extend(tz);

// ALL console timestamps on this page share one clock (defaultTimezone) and
// one shape: ISO to the millisecond. Table cells use
// <TimestampDisplay format={ISO_MS} skipCleanupDatetime />; `ts` is for inline
// (sentence-flow) spots where a block element doesn't fit.
const ISO_MS = 'YYYY-MM-DD HH:mm:ss.SSS';
const TIME_COLUMN_HEADER = `Time (${ChiroUpDayJsCommon.defaultTimezone})`;

// Manual runs are durable and carry a runId; poll only while this browser has
// requested runs without a terminal status. The manual refresh buttons stay.
const KICK_WATCH_INTERVAL_MS = MILLISECONDS.inSeconds(5);
const ts = (ms?: number | null) =>
  ms ? dayjs(ms).tz(ChiroUpDayJsCommon.defaultTimezone).format(ISO_MS) : '—';

const manualRunDescription = (run: ConnectorManualRun): string => {
  if (run.status === 'failed') return run.error ?? 'The manual run failed.';
  if (!run.summary) return 'Completed successfully.';

  if ('polled' in run.summary) {
    if (run.summary.outcome === 'locked') {
      return 'Another Receive run was active and accepted a follow-up pass.';
    }

    return `Completed: ${run.summary.polled} document(s) polled.`;
  }

  const parts: string[] = [];

  if (run.summary.reEnqueued) {
    parts.push(`${run.summary.reEnqueued} queued for transmission`);
  }
  if (run.summary.transmitted) {
    parts.push(`${run.summary.transmitted} transmitted locally`);
  }
  if (run.summary.enrollmentChecks) {
    parts.push(`${run.summary.enrollmentChecks} enrollment status check(s)`);
  }

  return parts.length ? `Completed: ${parts.join(', ')}.` : 'Completed: no pending exchanges.';
};

const Badge = ({ on, yes, no }: { on: boolean | null | undefined; yes: string; no: string }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
      on ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'
    }`}
  >
    {on ? yes : no}
  </span>
);

/**
 * Connection API ops console (plan §3g): service health + version, vendor
 * key status with the full rotation lifecycle, trace session control, and the
 * polling log viewer. Everything here renders connection-api endpoints; no
 * business logic lives in this file (UI is a viewer).
 */
const PageDescription = () => (
  <div className="space-y-4">
    <div className="space-y-2">
      <h3 className="font-semibold text-gray-900">Connection API operations console</h3>
      <p>
        The Connection API is ChiroUp&apos;s API-first clearinghouse integration: claims go out and
        remits/acks come back as JSON over vendor APIs (Stedi first) instead of X12 files over SFTP.
        This console is the operational surface for that service.
      </p>
      <ul className="ml-4 list-inside space-y-1">
        <li>
          <strong>Service</strong> — live health of the connection-api deployment. The version
          string changes on every deploy, so you can confirm a release actually landed. A loud red
          line appears here if the nightly activity prune looks dead. The rollups below it share one
          rule: recurring conditions collapse into one row with an occurrence count (&quot;#&quot;),
          so the feed stays readable under repetition.
        </li>
        <li>
          <strong>Errors</strong> — every recorded connector error (poll failures, transmit
          failures, webhook refusals, poll-lag SLO breaches, and post-Processed mapping/queue
          continuation findings). A post-Processed finding does not undo document acceptance; its
          durable downstream queue remains the recovery record. Rows whose action column shows
          &quot;Retry fetch&quot; are parked fetch-stubs: an inbound document the vendor fetch
          failed on, parked so it never blocks the poll feed. Retrying re-fetches from the vendor
          and upgrades the parked row in place; the outcome — or the refusal reason — appears next
          to the button. Error rows are history and stay listed after a successful retry.
        </li>
        <li>
          <strong>Webhook rings</strong> — vendor doorbell deliveries, rolled up per vendor per day.
          A ring only kicks the poll drain early; it never carries document content, so a quiet day
          here with healthy drain runs just means nothing arrived.
        </li>
        <li>
          <strong>Inbound drain runs</strong> — the poll → ingest sweeps, per vendor per day, with
          the latest run&apos;s numbers (refs polled, pages, processed / unrouted / duplicate).
          &quot;CAP DEFERRED&quot; means a deep backlog stopped at the page cap; &quot;TIME-BUDGET
          DEFERRED&quot; means the Lambda yielded before starting work it could not safely finish.
          Both resume from the persisted cursor. For an 835, Processed means every CLP is durably
          queued; claim posting may continue afterward until the queue is empty, no progress is
          possible, or the invocation deadline is reached.
        </li>
        <li>
          <strong>Transmit</strong> — a fleet-wide read-only overview of outbound claims still
          Pending, per vendor, plus any held transmit run lock (so the front- and back-ends don't
          compete). The queue normally transmits within moments, so a lingering count means
          something is stuck; the <strong>Transmit</strong>
          action lives on the Vendor card below (scoped to the selected vendor), and a lock held
          past its expiry can be force-cleared here.
        </li>
        <li>
          <strong>Vendor</strong> — one platform API key per vendor, held in Secrets Manager (never
          in the database; only slot <em>presence</em> is shown here). Rotation is staged: stage a
          candidate key, validate it against the vendor, promote it to active (the old key is kept
          as previous), or roll back. Every step is audited. &quot;Run credential check&quot; proves
          the active key with one cheap authenticated call — the same check a daily cron runs; only
          a true vendor rejection marks credentials invalid. <strong>Transmit</strong> re-drives
          THIS vendor&apos;s stale Pending claims; <strong>Receive</strong> kicks a poll → ingest
          for THIS vendor (the console watches for the results on its own for about a minute after a
          kick).
        </li>
        <li>
          <strong>Tracing</strong> — a self-expiring debug session per vendor: while active,
          connector work (submissions, polls, credential checks) writes step-by-step breadcrumbs you
          can watch below. Filters limit what gets <em>recorded</em>, not just displayed. Documents
          created while a matching session is live are stamped to keep tracing through the rest of
          their lifecycle — retries, acks, remits — even after the session ends. &quot;Trace one
          exchange&quot; stamps a single existing document the same way, with no session needed.
          Entries are PHI-free and prune after ~30 days.
        </li>
        <li>
          <strong>Queues</strong> (left nav) — inbound documents the funnel could not finish park
          there for a human: <em>Unrouted</em> (clinic could not be determined — inspect the exact
          signal/clinic matches, then assign or resolve it), <em>Unsupported</em> (no parser/handler
          for the document type yet — reprocess once support ships, or dismiss), and
          <em>Resolved</em> (closed, reversible via reopen). A failed assignment is recorded on the
          exchange and leaves only Resolve reason, Resolve, and Cancel for that attempt. Nothing is
          guessed and nothing is dropped.
        </li>
      </ul>
    </div>
  </div>
);

export const OpsConsole = () => {
  const { createToast } = use(ToastContext);
  // Reviewed rows are hidden across every feed until this is on (Brian, 2026-07-24).
  const [includeReviewed, setIncludeReviewed] = useState(false);
  // A review anywhere (error table or trace) bumps this so the Tracing stream —
  // a separate local buffer the console invalidation can't reach — drops the row.
  const [reviewedTick, setReviewedTick] = useState<{ id: number; n: number }>({ id: 0, n: 0 });
  const onReviewed = useCallback(
    (id: number) => setReviewedTick(prev => ({ id, n: prev.n + 1 })),
    []
  );
  const [pendingRunIds, setPendingRunIds] = useState<string[]>([]);
  const notifiedRunIds = useRef(new Set<string>());
  const onKicked = useCallback((result: ConnectorManualRunKickResult) => {
    setPendingRunIds(current =>
      current.includes(result.run.runId) ? current : [...current, result.run.runId]
    );
  }, []);

  // ONE read paints the page (plan §3f) — every section renders from it.
  const console = useConnectionApiConsole(
    includeReviewed,
    pendingRunIds.length ? KICK_WATCH_INTERVAL_MS : false
  );

  useEffect(() => {
    if (!pendingRunIds.length || !console.data?.runs?.length) return;

    const terminalRuns = console.data.runs.filter(
      run =>
        pendingRunIds.includes(run.runId) &&
        (run.status === 'completed' || run.status === 'failed') &&
        !notifiedRunIds.current.has(run.runId)
    );

    for (const run of terminalRuns) {
      notifiedRunIds.current.add(run.runId);
      const label = run.operation === 'receive' ? 'Receive' : 'Transmit';

      createToast({
        title: `${label} ${run.status === 'failed' ? 'Failed' : 'Complete'}`,
        description: manualRunDescription(run),
        type: run.status === 'failed' ? ToastTypes.Fail : ToastTypes.Success,
        duration: run.status === 'failed' ? 10_000 : 5_000,
      });
    }

    if (terminalRuns.length) {
      const terminalIds = new Set(terminalRuns.map(run => run.runId));
      setPendingRunIds(current => current.filter(runId => !terminalIds.has(runId)));
    }
  }, [console.data?.runs, createToast, pendingRunIds]);

  const vendorRows = console.data?.vendors ?? [];
  const [selectedVendor, setSelectedVendor] = useState<ConnectorVendorStatus | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const active = selectedVendor ?? vendorRows[0] ?? null;

  // One selection drives both sections; problem states are flagged in the
  // option text so they're visible even when another vendor is selected.
  const vendorOptions = vendorRows.map(v => ({
    text: `${v.vendor}${v.credentialsValid === false ? ' (INVALID)' : ''}${
      !v.registered ? ' (no connector)' : ''
    }`,
    value: v.vendor,
  }));
  const selectVendor = (name: string) =>
    setSelectedVendor(vendorRows.find(v => v.vendor === name) ?? null);

  return (
    <div className="space-y-8 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Connection API</h1>
        <div className="flex items-center gap-3">
          <div
            className="cursor-pointer"
            title="About this console."
            onClick={() => setIsHelpOpen(prev => !prev)}
          >
            <QuestionMarkCircleIcon className="h-6 w-6 text-gray-500 hover:text-gray-700" />
          </div>
          <Button
            color={ButtonColors.plain}
            onClick={() => console.refetch()}
            loading={console.isFetching}
            text="Check health"
          />
        </div>
      </div>

      {isHelpOpen ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
          <PageDescription />
        </div>
      ) : null}

      {/* ── Service status (service-wide — all vendors) ────────────────── */}
      <section className="rounded-lg border p-4">
        <div className="flex flex-row justify-between">
          <h2 className="mb-2 text-lg font-medium">Service</h2>
          <div className="flex items-center gap-3">
            <Checkbox
              name="include-reviewed"
              label="Include reviewed"
              value={includeReviewed}
              onChange={setIncludeReviewed}
            />
            <div
              className="cursor-pointer"
              title="Refresh the queue."
              onClick={() => console.refetch()}
            >
              <Icon
                icon={SharedIcons.refresh}
                className={`h-6 w-6 text-gray-500 hover:text-gray-700 ${
                  console.isFetching ? 'animate-spin' : ''
                }`}
              />
            </div>
          </div>
        </div>
        {console.isError ? (
          <p className="text-sm text-red-600">
            Console read failed — the service is unreachable or the route is down.
          </p>
        ) : console.data ? (
          <div className="flex gap-6 text-sm">
            <span>
              status <Badge on={console.data.service.ok} yes="ok" no="down" />
            </span>
            <span>
              stage <span className="font-mono">{console.data.service.stage}</span>
            </span>
            <span>
              version <span className="font-mono">{console.data.service.version}</span>
            </span>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Checking…</p>
        )}
        <PruneHealth sink={console.data?.activitySink} />
        <ServiceErrors rows={console.data?.errors.rows ?? []} onReviewed={onReviewed} />
        <ServiceWebhookRings rows={console.data?.webhooks?.rings ?? []} />
        <ServiceDrainActivity rows={console.data?.drains?.activity ?? []} />
        <TransmitPanel transmit={console.data?.transmit} loading={console.isLoading} />
      </section>

      {/* ── Vendor selector — drives the vendor-specific panels below ───── */}
      {vendorOptions.length > 1 ? (
        <div className="w-56">
          <Select
            name="vendor-select"
            label="Vendor"
            options={vendorOptions}
            value={active?.vendor}
            onChange={(val: string) => selectVendor(val)}
          />
        </div>
      ) : null}

      {/* ── Vendor ─────────────────────────────────────────────────────── */}
      <section className="rounded-lg border p-4">
        {console.isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : active ? (
          <VendorCard
            key={active.vendor}
            row={active}
            pending={console.data?.transmit?.pending?.[active.vendor] ?? 0}
            runs={console.data?.runs ?? []}
            execution={console.data?.service?.execution}
            database={console.data?.service?.stage}
            onRefresh={() => console.refetch()}
            refreshing={console.isFetching}
            onKicked={onKicked}
          />
        ) : (
          <p className="text-sm text-gray-500">No platform vendors found.</p>
        )}
      </section>

      {/* ── Tracing ────────────────────────────────────────────────────── */}
      {active ? (
        <TracePanel
          vendor={active.vendor}
          integrationId={active.integrationId}
          traceConfig={console.data?.traceConfigs?.[active.vendor] ?? null}
          includeReviewed={includeReviewed}
          reviewedTick={reviewedTick}
          onReviewed={onReviewed}
        />
      ) : null}
    </div>
  );
};

const VendorCard = ({
  row,
  pending,
  runs,
  execution,
  database,
  onRefresh,
  refreshing,
  onKicked,
}: {
  row: ConnectorVendorStatus;
  /** This vendor's Pending outbound transmits — gates the backstop button. */
  pending: number;
  runs: ConnectorManualRun[];
  /** This backend's runtime "combo" — which execution + database you're driving. */
  execution?: ConnectorExecution;
  database?: string;
  // The run-now buttons report what they DID, not the fresh state; a refresh
  // re-reads the console (shares the single query, so it repaints the page).
  onRefresh: () => void;
  refreshing: boolean;
  /** Track the exact durable run until it completes or fails. */
  onKicked: (result: ConnectorManualRunKickResult) => void;
}) => {
  const [candidateKey, setCandidateKey] = useState('');
  const runCheck = useRunCredentialCheck();
  const runBackstop = useRunTransmitBackstop();
  const runDrain = useRunInboundDrain();
  const togglePolling = useTogglePolling();
  // Automation polling state for THIS backend's combo (its own execution).
  const pollingOn = execution ? row.pollingEnabled?.[execution] === true : false;
  const stage = useStageCandidate();
  const validate = useValidateCandidate();
  const promote = usePromoteCandidate();
  const rollback = useRollbackCredentials();
  const args = { vendor: row.vendor, integrationId: row.integrationId };
  const requestedTransmitRun = runBackstop.data
    ? (runs.find(run => run.runId === runBackstop.data.run.runId) ?? runBackstop.data.run)
    : null;
  const requestedReceiveRun = runDrain.data
    ? (runs.find(run => run.runId === runDrain.data.run.runId) ?? runDrain.data.run)
    : null;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-medium">Vendor — {row.vendor}</h2>
        <div className="flex items-center gap-2">
          <div className="cursor-pointer" title="Refresh this vendor." onClick={() => onRefresh()}>
            <Icon
              icon={SharedIcons.refresh}
              className={`h-6 w-6 text-gray-500 hover:text-gray-700 ${
                refreshing ? 'animate-spin' : ''
              }`}
            />
          </div>
          <Button
            color={ButtonColors.plain}
            loading={runCheck.isLoading}
            disabled={!row.registered}
            onClick={() => runCheck.mutate(row.vendor)}
            text="Run credential check"
          />
          <Button
            color={ButtonColors.primary}
            icon={Icons.playUp}
            loading={runBackstop.isLoading}
            // Nothing to re-drive when this vendor has no Pending transmits.
            disabled={!row.registered || !pending}
            onClick={() => runBackstop.mutate(row.vendor, { onSuccess: onKicked })}
            text={`Transmit${pending ? ` (${pending})` : ''}`}
          />
          <Button
            color={ButtonColors.primary}
            icon={Icons.playDown}
            loading={runDrain.isLoading}
            // Always available: draining is how inbound documents are discovered.
            disabled={!row.registered}
            onClick={() => runDrain.mutate(row.vendor, { onSuccess: onKicked })}
            text="Receive"
          />
        </div>
      </div>

      {runBackstop.data ? (
        <p className="mb-2 text-xs text-gray-600">
          Transmit run <span className="break-all font-mono">{runBackstop.data.run.runId}</span>:{' '}
          {requestedTransmitRun?.status ?? 'queued'}.
        </p>
      ) : null}

      {runDrain.data ? (
        <p className="mb-2 text-xs text-gray-600">
          Receive run <span className="break-all font-mono">{runDrain.data.run.runId}</span>:{' '}
          {requestedReceiveRun?.status ?? 'queued'}.
        </p>
      ) : null}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">integrationId {row.integrationId}</span>
          {!row.registered ? (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              no connector registered
            </span>
          ) : null}
          <Badge
            on={row.credentialsValid === true}
            yes="credentials valid"
            no={row.credentialsValid === false ? 'credentials INVALID' : 'unchecked'}
          />
          <span className="text-xs text-gray-500">checked {ts(row.credentialsLastCheckedAt)}</span>
          <span className="text-xs text-gray-500">last clean poll {ts(row.lastProcessedAt)}</span>
        </div>
        <div className="flex gap-2 text-xs">
          <Badge on={row.secret.hasActive} yes="active key" no="no active key" />
          <Badge on={row.secret.hasCandidate} yes="candidate staged" no="no candidate" />
          <Badge on={row.secret.hasPrevious} yes="previous kept" no="no previous" />
        </div>
      </div>

      {/* ── Automation polling gate — THIS combo (execution·database) ─────── */}
      <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium">Automation polling</span>
          <span className="text-xs text-gray-500">
            this backend: <strong>{execution ?? '—'}</strong> · <strong>{database ?? '—'}</strong>
          </span>
          <Badge on={pollingOn} yes="polling ON" no="polling OFF" />
          <Button
            color={pollingOn ? ButtonColors.plain : ButtonColors.primary}
            loading={togglePolling.isLoading}
            disabled={!row.registered || !execution}
            onClick={() => togglePolling.mutate({ vendor: row.vendor, enabled: !pollingOn })}
            text={pollingOn ? 'Turn polling off' : 'Turn polling on'}
          />
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Controls the scheduled cron + webhook doorbell for THIS combo ({execution ?? '—'} ·{' '}
          {database ?? '—'}) only. The manual &quot;Run inbound drain now&quot; button always works.
          Default is OFF; other combos are controlled from their own env&apos;s admin app.
        </p>
      </div>

      <div className="mt-2 text-xs text-gray-500">
        staged {ts(row.rotation.stagedAt)} by {row.rotation.stagedBy ?? '—'} · validated{' '}
        {ts(row.rotation.validatedAt)} ({row.rotation.validatedResult ?? '—'}) · promoted{' '}
        {ts(row.rotation.promotedAt)} by {row.rotation.promotedBy ?? '—'} · rolled back{' '}
        {ts(row.rotation.rolledBackAt)}
      </div>

      {!row.registered ? (
        <p className="mt-2 text-xs text-amber-700">
          This platform row has no connector implementation in code — rotation and validation are
          unavailable. If the vendor was decommissioned, close out the row; if this is unexpected, a
          registry entry is missing.
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="w-96">
          <Input
            name={`candidate-${row.vendor}`}
            label="Candidate API key"
            value={candidateKey}
            onChange={(val: string) => setCandidateKey(val)}
          />
        </div>
        <Button
          color={ButtonColors.plain}
          loading={stage.isLoading}
          disabled={!row.registered || !candidateKey.trim()}
          onClick={() =>
            stage.mutate(
              { ...args, apiKey: candidateKey },
              { onSuccess: () => setCandidateKey('') }
            )
          }
          text="Stage"
        />
        <Button
          color={ButtonColors.plain}
          loading={validate.isLoading}
          disabled={!row.registered || !row.secret.hasCandidate}
          onClick={() => validate.mutate(args)}
          text="Validate candidate"
        />
        <Button
          color={ButtonColors.primary}
          loading={promote.isLoading}
          disabled={!row.registered || !row.secret.hasCandidate}
          onClick={() => {
            if (window.confirm(`Promote ${row.vendor}'s candidate key to ACTIVE?`)) {
              promote.mutate(args);
            }
          }}
          text="Promote"
        />
        <Button
          color={ButtonColors.red}
          loading={rollback.isLoading}
          disabled={!row.registered || !row.secret.hasPrevious}
          onClick={() => {
            if (window.confirm(`ROLL BACK ${row.vendor} to the previous key?`)) {
              rollback.mutate(args);
            }
          }}
          text="Roll back"
        />
        {validate.data ? (
          <span className="text-sm">
            candidate: <Badge on={validate.data.valid} yes="valid" no="REJECTED" />
          </span>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Dead-prune detector (Brian, 2026-07-17): the console reads the sink with NO
 * time bound — the prune cron owns retention — so the console must SAY when
 * the prune isn't doing its job. Oldest row older than retention + slack =
 * a loud red line, not a silently ballooning table.
 */
const PRUNE_SLACK_DAYS = 2;

const PruneHealth = ({ sink }: { sink?: { oldestMs: number | null; retentionDays: number } }) => {
  if (!sink?.oldestMs) return null;

  const ageDays = Math.floor((Date.now() - sink.oldestMs) / MILLISECONDS.DAY);

  if (ageDays <= sink.retentionDays + PRUNE_SLACK_DAYS) return null;

  return (
    <p className="mt-3 text-sm font-semibold text-red-700">
      Activity prune appears DEAD: the oldest sink row is {ageDays} days old (retention{' '}
      {sink.retentionDays}d). Check the startConnectorTracePrune cron.
    </p>
  );
};

/**
 * Webhook rings (plan §3g carve-out #2, INFRA-4) — the doorbell's visibility:
 * ONE row per vendor per day, `occurrences` = the day's ring count (retries
 * included; Brian, 2026-07-17). Count-first like the errors, neutral tone
 * (rings are operations, not incidents); the shared DataTable paginates.
 */
const ringColumns: ColumnDef<ConnectorTraceRow, unknown>[] = [
  {
    accessorKey: 'createdAt',
    header: 'Day (last ring)',
    cell: ({ row }) => (
      <TimestampDisplay ts={row.original.createdAt} format={ISO_MS} skipCleanupDatetime />
    ),
    enableSorting: false,
    meta: { className: 'whitespace-nowrap font-mono' },
  },
  {
    accessorKey: 'vendor',
    header: 'Vendor',
    enableSorting: false,
    meta: { className: 'whitespace-nowrap' },
  },
  {
    accessorKey: 'occurrences',
    header: 'Rings',
    cell: ({ row }) => row.original.occurrences ?? 1,
    enableSorting: false,
    meta: { align: 'right' as const, className: 'tabular-nums' },
  },
  {
    id: 'lastTransaction',
    header: 'Last transaction',
    cell: ({ row }) =>
      String((row.original.data as { lastTransactionId?: string } | null)?.lastTransactionId ?? ''),
    enableSorting: false,
    meta: { className: 'font-mono' },
  },
];

const ServiceWebhookRings = ({ rows }: { rows: ConnectorTraceRow[] }) => {
  const [open, setOpen] = useState(false);

  if (!rows.length) return null;

  const totalRings = rows.reduce((n, r) => n + (r.occurrences ?? 1), 0);
  const oldest = Math.min(...rows.map(r => r.createdAt));
  const spanDays = Math.max(1, Math.ceil((Date.now() - oldest) / MILLISECONDS.DAY));

  return (
    <div className="mt-3">
      <button
        type="button"
        className="text-sm font-medium text-gray-700 hover:underline"
        onClick={() => setOpen(prev => !prev)}
      >
        {totalRings} webhook ring{totalRings === 1 ? '' : 's'} in the last {spanDays} day
        {spanDays === 1 ? '' : 's'} {open ? '▾' : '▸'}
      </button>
      {open ? (
        <div className="mt-2 [&_td]:align-top!">
          <DataTable
            columns={ringColumns}
            data={rows}
            size="compact"
            pagination={{ pageSize: 50, pageSizeOptions: [50, 100] }}
          />
        </div>
      ) : null}
    </div>
  );
};

/**
 * Inbound-drain activity (carve-out #3, INFRA-6 — Brian, 2026-07-21: a
 * console.info is not visibility): ONE rollup row per vendor per day,
 * `occurrences` = the day's NOISY runs (quiet runs never write), `data` =
 * the latest run's counts. Live trace breadcrumbs are excluded by the
 * backend. Neutral tone — moved documents are operations.
 */
const drainColumns: ColumnDef<ConnectorTraceRow, unknown>[] = [
  {
    accessorKey: 'createdAt',
    header: 'Day (last run)',
    cell: ({ row }) => (
      <TimestampDisplay ts={row.original.createdAt} format={ISO_MS} skipCleanupDatetime />
    ),
    enableSorting: false,
    meta: { className: 'whitespace-nowrap font-mono' },
  },
  {
    accessorKey: 'vendor',
    header: 'Vendor',
    enableSorting: false,
    meta: { className: 'whitespace-nowrap' },
  },
  {
    accessorKey: 'occurrences',
    header: 'Runs',
    cell: ({ row }) => row.original.occurrences ?? 1,
    enableSorting: false,
    meta: { align: 'right' as const, className: 'tabular-nums' },
  },
  {
    id: 'latestRun',
    header: 'Latest run',
    cell: ({ row }) => {
      // The shared summary shape — the same type the sink writer takes, so
      // this reader can't drift from the producer again.
      const d = (row.original.data ?? {}) as Partial<ConnectorDrainRunSummary>;
      const outcomes = Object.entries(d.outcomes ?? {})
        .map(([k, n]) => `${k} ${n}`)
        .join(', ');
      const deferred =
        d.deferred === 'page-cap'
          ? ' — CAP DEFERRED'
          : d.deferred === 'time-budget'
            ? ' — TIME-BUDGET DEFERRED'
            : '';

      return `${d.polled ?? 0} polled / ${d.pages ?? 0} pages${outcomes ? ` — ${outcomes}` : ''}${deferred}`;
    },
    enableSorting: false,
    meta: { className: 'font-mono' },
  },
];

const ServiceDrainActivity = ({ rows }: { rows: ConnectorTraceRow[] }) => {
  const [open, setOpen] = useState(false);

  if (!rows.length) return null;

  const totalRuns = rows.reduce((n, r) => n + (r.occurrences ?? 1), 0);
  const oldest = Math.min(...rows.map(r => r.createdAt));
  const spanDays = Math.max(1, Math.ceil((Date.now() - oldest) / MILLISECONDS.DAY));

  return (
    <div className="mt-3">
      <button
        type="button"
        className="text-sm font-medium text-gray-700 hover:underline"
        onClick={() => setOpen(prev => !prev)}
      >
        {totalRuns} inbound drain run{totalRuns === 1 ? '' : 's'} in the last {spanDays} day
        {spanDays === 1 ? '' : 's'} {open ? '▾' : '▸'}
      </button>
      {open ? (
        <div className="mt-2 [&_td]:align-top!">
          <DataTable
            columns={drainColumns}
            data={rows}
            size="compact"
            pagination={{ pageSize: 50, pageSizeOptions: [50, 100] }}
          />
        </div>
      ) : null}
    </div>
  );
};

/**
 * Always-recorded operational errors (plan §3g carve-out) — count-first: a
 * red line only when nonzero, expanding to the shared paginated DataTable.
 */
const traceColumns: ColumnDef<ConnectorTraceRow, unknown>[] = [
  {
    accessorKey: 'createdAt',
    header: TIME_COLUMN_HEADER,
    cell: ({ row }) => (
      <TimestampDisplay ts={row.original.createdAt} format={ISO_MS} skipCleanupDatetime />
    ),
    enableSorting: false,
    meta: { className: 'whitespace-nowrap font-mono' },
  },
  {
    accessorKey: 'step',
    header: 'Step',
    enableSorting: false,
    meta: { className: 'whitespace-nowrap font-mono' },
  },
  { accessorKey: 'message', header: 'Message', enableSorting: false },
  {
    accessorKey: 'clinicId',
    header: 'Clinic',
    enableSorting: false,
    meta: { align: 'right' as const, className: 'font-mono' },
  },
  {
    accessorKey: 'docType',
    header: 'Doc',
    cell: ({ row }) => row.original.docType ?? '',
    enableSorting: false,
    meta: { className: 'whitespace-nowrap font-mono' },
  },
  // exchangeId + the inline data JSON moved into the click-through detail modal.
];

const errorColumns: ColumnDef<ConnectorTraceRow, unknown>[] = [
  {
    accessorKey: 'createdAt',
    header: TIME_COLUMN_HEADER,
    cell: ({ row }) => (
      <TimestampDisplay ts={row.original.createdAt} format={ISO_MS} skipCleanupDatetime />
    ),
    enableSorting: false,
    meta: { className: 'whitespace-nowrap font-mono' },
  },
  {
    accessorKey: 'vendor',
    header: 'Vendor',
    enableSorting: false,
    meta: { className: 'whitespace-nowrap' },
  },
  {
    accessorKey: 'clinicId',
    header: 'Clinic',
    enableSorting: false,
    meta: { align: 'right' as const, className: 'font-mono' },
  },
  {
    accessorKey: 'occurrences',
    header: '#',
    cell: ({ row }) => row.original.occurrences ?? 1,
    enableSorting: false,
    meta: { align: 'right' as const, className: 'tabular-nums' },
  },
  // Only the message wraps; everything else stays on one line. The exchangeId
  // and per-row actions moved into the click-through detail modal (item 1).
  { accessorKey: 'message', header: 'Message', enableSorting: false },
];

/**
 * Click-through detail for one error/activity row (item 1). The row itself is
 * already in hand (the console read returned it); the EXCHANGE is pulled on
 * demand by id (only when the row has one). Retry/Retry+Trace show from the
 * SSOT `retryable` flag the read stamps — the UI never decides retryability.
 */
const ActivityDetailModal = ({
  row,
  onClose,
  onReviewed,
}: {
  row: ConnectorTraceRow;
  onClose: () => void;
  /** Fired after a mark/restore so the Tracing stream can drop the stale local row. */
  onReviewed?: (id: number) => void;
}) => {
  const exchangeId = row.exchangeId ?? null;
  const exchangeQuery = useConnectorExchange(exchangeId);
  const retry = useRetryExchange();
  const review = useReviewActivity();
  const exchange: ConnectorConsoleExchange | null = exchangeQuery.data?.exchange ?? null;
  const isReviewed = row.step === 'reviewed';
  const [downloadingPayload, setDownloadingPayload] = useState(false);
  const [payloadError, setPayloadError] = useState<string | null>(null);

  // Item 8: the stored document (sidecar payload) as pretty JSON, saved as a
  // file — the connector counterpart of the legacy X12 download. Offered only
  // when the exchange read said `hasPayload` (server decides, UI renders).
  const downloadPayload = async () => {
    if (!exchangeId) return;

    setDownloadingPayload(true);
    setPayloadError(null);

    try {
      const res = await connectionApiService.fetchExchangePayload(exchangeId);
      // The server says what the document IS (stored mimeType); the extension
      // follows it — .json for JSON reports, .txt for anything verbatim.
      const url = URL.createObjectURL(new Blob([res.payload], { type: res.contentType }));
      const a = document.createElement('a');

      a.href = url;
      a.download = `${exchangeId}.${res.contentType === 'application/json' ? 'json' : 'txt'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setPayloadError(connectorErrorMessage(e, 'Payload download failed.'));
    } finally {
      setDownloadingPayload(false);
    }
  };

  const history = Array.isArray(
    (exchange?.meta as { dispositionHistory?: unknown })?.dispositionHistory
  )
    ? (exchange!.meta as { dispositionHistory: { text?: string; data?: unknown }[] })
        .dispositionHistory
    : [];

  const runRetry = (trace: boolean) => {
    if (exchangeId) retry.mutate({ exchangeId, trace });
  };

  return (
    <Modal
      isOpen
      close={onClose}
      size="3xl"
      header={{ title: 'Connector activity detail' }}
      footer={{
        buttons: [
          {
            text: 'Retry',
            color: ButtonColors.primary,
            loading: retry.isLoading,
            disabled: retry.isLoading,
            visible: !!exchange?.retryable,
            onClick: () => runRetry(false),
          },
          {
            text: 'Retry + Trace',
            color: ButtonColors.plain,
            loading: retry.isLoading,
            disabled: retry.isLoading,
            visible: !!exchange?.retryable,
            onClick: () => runRetry(true),
          },
          {
            text: 'Download payload',
            color: ButtonColors.plain,
            loading: downloadingPayload,
            disabled: downloadingPayload,
            visible: !!exchange?.hasPayload,
            onClick: () => downloadPayload(),
          },
          {
            // Mark reviewed to hide the row from its feed, or restore it.
            text: isReviewed ? 'Clear review' : 'Reviewed',
            color: ButtonColors.plain,
            loading: review.isLoading,
            disabled: review.isLoading,
            onClick: () =>
              review.mutate(
                { id: row.id, reviewed: !isReviewed },
                {
                  onSuccess: () => {
                    onReviewed?.(row.id);
                    onClose();
                  },
                }
              ),
          },
          { text: 'Close', color: ButtonColors.plain, onClick: onClose },
        ],
      }}
    >
      <div className="space-y-4 pb-2">
        <HashCardDisplay
          header="Activity"
          hash={{
            time: ts(row.createdAt),
            vendor: row.vendor,
            clinicId: String(row.clinicId),
            step: row.step,
            dedupKey: row.dedupKey ?? '—',
            occurrences: String(row.occurrences ?? 1),
            exchangeId: row.exchangeId ?? '—',
            message: row.message,
          }}
        />
        {row.data ? (
          <div>
            <div className="mb-1 text-sm font-medium">Data</div>
            <pre className="max-h-40 overflow-auto rounded bg-gray-50 p-2 text-xs">
              {ChiroUpJSON.pretty(row.data)}
            </pre>
          </div>
        ) : null}

        {exchangeId ? (
          exchangeQuery.isLoading ? (
            <p className="text-sm text-gray-500">Loading exchange…</p>
          ) : exchange ? (
            <>
              <HashCardDisplay
                header="Exchange"
                hash={{
                  exchangeId: exchange.exchangeId,
                  type: exchange.type,
                  disposition: exchange.disposition,
                  direction: exchange.source ?? '—',
                  vendor: exchange.connectorVendor ?? '—',
                  partnerTransactionId: exchange.partnerTransactionId ?? '—',
                  retryable: exchange.retryable ? 'yes' : 'no',
                }}
              />
              {history.length ? (
                <div>
                  <div className="mb-1 text-sm font-medium">Disposition history</div>
                  <ol className="space-y-1 text-xs">
                    {history.map((h, i) => (
                      <li key={i} className="rounded bg-gray-50 p-2">
                        <div>{h.text ?? '(no text)'}</div>
                        {h.data ? (
                          <pre className="mt-1 overflow-auto">{ChiroUpJSON.pretty(h.data)}</pre>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                </div>
              ) : null}
              <div>
                <div className="mb-1 text-sm font-medium">Meta</div>
                <pre className="max-h-64 overflow-auto rounded bg-gray-50 p-2 text-xs">
                  {ChiroUpJSON.pretty(exchange.meta)}
                </pre>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">Exchange {exchangeId} not found.</p>
          )
        ) : null}

        {retry.isSuccess ? (
          <p className="text-sm text-green-700">
            {retry.data?.direction === 'outbound'
              ? 'Queued for retry'
              : `Retry: ${retry.data?.outcome}`}
            {retry.data?.traced ? ' (tracing on for its next run)' : ''}.
          </p>
        ) : null}
        {retry.isError ? (
          <p className="text-sm text-red-600">
            {connectorErrorMessage(retry.error, 'Retry failed.')}
          </p>
        ) : null}
        {payloadError ? <p className="text-sm text-red-600">{payloadError}</p> : null}
      </div>
    </Modal>
  );
};

const ServiceErrors = ({
  rows,
  onReviewed,
}: {
  rows: ConnectorTraceRow[];
  onReviewed: (id: number) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<ConnectorTraceRow | null>(null);

  if (!rows.length) return null;

  // All recorded errors are shown (the sink prunes at ~30 days); the label
  // reports the actual span, from the oldest row's age.
  const oldest = Math.min(...rows.map(r => r.createdAt));
  const spanDays = Math.max(1, Math.ceil((Date.now() - oldest) / MILLISECONDS.DAY));

  return (
    <div className="mt-3">
      <button
        type="button"
        className="text-sm font-medium text-red-700 hover:underline"
        onClick={() => setOpen(prev => !prev)}
      >
        {rows.length} error{rows.length === 1 ? '' : 's'} in the last {spanDays} day
        {spanDays === 1 ? '' : 's'} {open ? '▾' : '▸'}
      </button>
      {open ? (
        <div className="mt-2 [&_td]:!align-top [&_tbody_tr]:cursor-pointer">
          <DataTable
            columns={errorColumns}
            data={rows}
            size="compact"
            pagination={{ pageSize: 50, pageSizeOptions: [50, 100] }}
            onRowClick={r => setSelected(r)}
          />
        </div>
      ) : null}
      {selected ? (
        <ActivityDetailModal
          row={selected}
          onClose={() => setSelected(null)}
          onReviewed={onReviewed}
        />
      ) : null}
    </div>
  );
};

/**
 * Fleet-wide READ-ONLY overview of Pending connector transmits + any held
 * backstop locks (plan §3c.1a). The run-now action lives per-vendor on the
 * Vendor card (its panel is vendor-scoped); a Service-level "run all" would be
 * a separate action added here. Clearing a dead vendor's lock stays here,
 * beside where the locks are shown.
 */
const TransmitPanel = ({
  transmit,
  loading,
}: {
  transmit?: {
    pending: Record<string, number>;
    locks: Record<string, ConnectorRunLockInfo | null>;
  };
  loading: boolean;
}) => {
  const clearLock = useClearTransmitLock();
  const purgeQueue = usePurgeTransmitQueue();
  const { createToast } = use(ToastContext);

  const pending = transmit?.pending ?? {};
  const locks = transmit?.locks ?? {};
  const pendingTotal = Object.values(pending).reduce((a, b) => a + b, 0);
  const heldLocks = Object.entries(locks).filter(([, lock]) => lock && lock.expiresAt > Date.now());

  // The purge window (AWS may keep deleting for up to 60s, and allows one
  // purge per queue per 60s — it enforces both itself; PurgeQueueInProgress
  // comes back as a clean refusal). The countdown tells the operator when it
  // is safe to Transmit again AND when the button re-enables.
  const nextPurgeAllowedAt = purgeQueue.data?.nextAllowedAt ?? 0;
  const [now, setNow] = useState(() => Date.now());
  const purgeCoolingDown = nextPurgeAllowedAt > now;

  useEffect(() => {
    if (!purgeCoolingDown) return undefined;

    const tick = window.setInterval(() => setNow(Date.now()), 1_000);

    return () => window.clearInterval(tick);
  }, [purgeCoolingDown]);

  const purge = () => {
    if (
      !window.confirm(
        'Purge the AWS SQS transmit queue (all vendors)? Every queued SQS message is ' +
          'deleted — ghosts included, and AWS may keep deleting for up to 60 seconds. ' +
          'IntegrationExchange rows are NOT touched; wait for the countdown to finish ' +
          'before pressing Transmit to re-enqueue the Pending ones.'
      )
    ) {
      return;
    }

    purgeQueue.mutate(undefined, {
      onSuccess: result => {
        createToast({
          title: result.purged ? 'AWS SQS Purge Started' : 'AWS SQS Purge Not Started',
          description: result.message,
          type: result.purged ? ToastTypes.Success : ToastTypes.Fail,
          duration: 8_000,
        });
      },
    });
  };

  return (
    <div className="mt-4 border-t border-gray-950/5 pt-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">
          Transmit{' '}
          <span className={`font-normal ${pendingTotal ? 'text-amber-700' : 'text-gray-500'}`}>
            {loading ? '…' : `${pendingTotal} pending`}
          </span>
        </h3>
        <Button
          color={ButtonColors.plain}
          loading={purgeQueue.isLoading}
          disabled={purgeCoolingDown}
          onClick={purge}
          text={
            purgeCoolingDown
              ? `Purge AWS SQS queue (${Math.ceil((nextPurgeAllowedAt - now) / 1000)}s)`
              : 'Purge AWS SQS queue'
          }
        />
      </div>

      {Object.keys(pending).length ? (
        <div className="text-sm">
          {Object.entries(pending).map(([vendor, count]) => (
            <span key={vendor} className="mr-4">
              {vendor}: <span className="font-mono">{count}</span>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">No connector claims waiting to transmit.</p>
      )}

      {heldLocks.map(([vendor, lock]) => (
        <div key={vendor} className="mt-2 flex items-center gap-2 text-xs text-amber-700">
          <span>
            {vendor}: backstop running — held by <span className="font-mono">{lock!.holder}</span>{' '}
            since {ts(lock!.at)}, expires {ts(lock!.expiresAt)}
          </span>
          <Button
            color={ButtonColors.plain}
            loading={clearLock.isLoading}
            onClick={() => {
              if (
                window.confirm(`Force-clear ${vendor}'s transmit lock? Only if the holder is dead.`)
              ) {
                clearLock.mutate(vendor);
              }
            }}
            text="Clear lock"
          />
        </div>
      ))}
    </div>
  );
};

const DURATIONS = [
  { text: '5 minutes', value: 5 },
  { text: '15 minutes', value: 15 },
  { text: '60 minutes', value: 60 },
];

const INTERVALS = [
  { text: '5s', value: 5 },
  { text: '10s', value: 10 },
  { text: '30s', value: 30 },
  { text: 'Manual', value: 0 },
];

const DIRECTIONS = [
  { text: 'Both', value: '' },
  { text: 'Inbound', value: 'inbound' },
  { text: 'Outbound', value: 'outbound' },
];

const DOC_TYPES = [
  { text: 'All', value: '' },
  ...Object.values(IntegrationDocumentType).map(d => ({ text: d, value: d as string })),
];

const TracePanel = ({
  vendor,
  integrationId,
  traceConfig,
  includeReviewed,
  reviewedTick,
  onReviewed,
}: {
  vendor: string;
  integrationId: number;
  traceConfig: { config: { traceUntil: number } | null; active: boolean } | null;
  includeReviewed: boolean;
  /** Bumped when ANY row is reviewed; carries the reviewed row's id so the stream can drop it. */
  reviewedTick: { id: number; n: number };
  onReviewed: (id: number) => void;
}) => {
  const setConfig = useSetTraceConfig(vendor, integrationId);
  const invalidateConsole = useInvalidateConsole();

  const [duration, setDuration] = useState(15);
  const [clinicIdsCsv, setClinicIdsCsv] = useState('');
  const [intervalSec, setIntervalSec] = useState(10);
  const [paused, setPaused] = useState(false);
  const [direction, setDirection] = useState('');
  const [docType, setDocType] = useState('');
  const [rows, setRows] = useState<ConnectorTraceRow[]>([]);
  const [selected, setSelected] = useState<ConnectorTraceRow | null>(null);
  const sinceIdRef = useRef(0);

  const [stampId, setStampId] = useState('');
  const [stampDuration, setStampDuration] = useState(15);
  const [stamping, setStamping] = useState(false);
  const [stampedUntil, setStampedUntil] = useState<{ id: string; until: number } | null>(null);

  // Trace-this-one-exchange (plan §3g): stamps traceUntil on an existing
  // IntegrationExchange row — its processing traces with NO session on.
  const stampExchange = async () => {
    setStamping(true);

    try {
      const res = await connectionApiService.stampExchange(stampId.trim(), stampDuration);

      setStampedUntil({ id: res.exchangeId, until: res.traceUntil });
      setStampId('');
    } catch {
      setStampedUntil(null);
      window.alert(`No exchange '${stampId.trim()}' found to stamp.`);
    } finally {
      setStamping(false);
    }
  };

  const enable = () => {
    const clinicIds = clinicIdsCsv
      .split(',')
      .map(s => Number(s.trim()))
      .filter(Number.isInteger)
      .filter(n => n > 0);

    // Filters constrain what the session WRITES — only matching activity is
    // recorded, so the viewer is just a dumb feed of the session's rows.
    const filters = {
      ...(clinicIds.length ? { clinicIds } : {}),
      ...(docType ? { docTypes: [docType as IntegrationDocumentType] } : {}),
      ...(direction ? { direction: direction as ConnectorTraceDirection } : {}),
    };

    setConfig.mutate({
      durationMinutes: duration,
      filters: Object.keys(filters).length ? filters : undefined,
    });
  };

  const disable = () => setConfig.mutate({ durationMinutes: 0 });

  const poll = useCallback(async () => {
    const { rows: fresh } = await connectionApiService.traceLogs(vendor, integrationId, {
      sinceId: sinceIdRef.current,
      limit: 100,
      includeReviewed,
    });

    if (fresh.length) {
      sinceIdRef.current = Math.max(...fresh.map(r => r.id));
      // Newest-first: fresh rows (already desc) prepend; cap memory at 500.
      setRows(prev => [...fresh, ...prev].slice(0, 500));
    }
  }, [vendor, integrationId, includeReviewed]);

  useEffect(() => {
    // Reset the viewer when the vendor changes OR the reviewed filter flips
    // (the sinceId cursor already skipped hidden rows; re-read from scratch).
    sinceIdRef.current = 0;
    setRows([]);
  }, [vendor, integrationId, includeReviewed]);

  useEffect(() => {
    // A row reviewed anywhere (this panel or the error table) leaves the feed;
    // the incremental poll never re-fetches an already-shown row, so drop the
    // stale local copy here. `includeReviewed` keeps it (the viewer wants it).
    if (reviewedTick.id && !includeReviewed) {
      setRows(prev => prev.filter(r => r.id !== reviewedTick.id));
    }
  }, [reviewedTick, includeReviewed]);

  const sessionActive = !!traceConfig?.active;
  const traceUntil = traceConfig?.config?.traceUntil ?? 0;

  useEffect(() => {
    // No session = nothing being written, so nothing to poll for. Manual
    // (intervalSec 0) and Pause also stop the clock; the refresh button
    // still polls on demand.
    if (!sessionActive || paused || !intervalSec) return undefined;

    const id = window.setInterval(() => {
      poll().catch(() => undefined);
    }, MILLISECONDS.inSeconds(intervalSec));

    poll().catch(() => undefined);

    return () => window.clearInterval(id);
  }, [poll, intervalSec, paused, sessionActive]);

  useEffect(() => {
    // Sessions self-expire server-side; re-read the console just past
    // traceUntil so the label flips to "off" and polling stops on its own.
    // (Codex review 2026-07-14: setConfig.reset() only cleared mutation
    // state — the expired session stayed "active" until a manual refresh.)
    if (!sessionActive || !traceUntil) return undefined;

    const id = window.setTimeout(
      () => invalidateConsole(),
      Math.max(0, traceUntil - Date.now()) + MILLISECONDS.SECOND
    );

    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionActive, traceUntil]);

  const activeUntil = useMemo(() => {
    return sessionActive ? dayjs(traceUntil).format(ISO_MS) : null;
  }, [sessionActive, traceUntil]);

  return (
    <section className="rounded-lg border p-4">
      <h2 className="mb-4 text-lg font-medium">
        Tracing — {vendor}{' '}
        {activeUntil ? (
          <span className="text-sm font-normal text-green-700">
            session active until {activeUntil}
          </span>
        ) : (
          <span className="text-sm font-normal text-gray-500">off</span>
        )}
      </h2>
      <div className="mb-4 flex items-end gap-2">
        <div className="w-40">
          <Select
            name="trace-duration"
            label="Duration"
            options={DURATIONS}
            value={duration}
            onChange={(val: number) => setDuration(val)}
          />
        </div>
        <div className="w-32">
          <Select
            name="trace-direction"
            label="Direction"
            options={DIRECTIONS}
            value={direction}
            onChange={(val: string) => setDirection(val ?? '')}
          />
        </div>
        <div className="w-32">
          <Select
            name="trace-doc-type"
            label="Doc type"
            options={DOC_TYPES}
            value={docType}
            onChange={(val: string) => setDocType(val ?? '')}
          />
        </div>
        <div className="w-56">
          <Input
            name="trace-clinics"
            label="Clinic ids (csv, optional)"
            value={clinicIdsCsv}
            onChange={(val: string) => setClinicIdsCsv(val)}
          />
        </div>
        <Button
          color={ButtonColors.primary}
          loading={setConfig.isLoading}
          onClick={enable}
          text={sessionActive ? 'Extend' : 'Start'}
        />
        <Button
          color={ButtonColors.plain}
          disabled={!sessionActive}
          onClick={disable}
          text="Stop"
        />
      </div>

      <div className="mb-4 flex items-end gap-2">
        <div className="w-72">
          <Input
            name="stamp-exchange-id"
            label="Trace one exchange (no session needed)"
            value={stampId}
            onChange={(val: string) => setStampId(val)}
          />
        </div>
        <div className="w-40">
          <Select
            name="stamp-duration"
            label=""
            options={DURATIONS}
            value={stampDuration}
            onChange={(val: number) => setStampDuration(val)}
          />
        </div>
        <Button
          color={ButtonColors.plain}
          loading={stamping}
          disabled={!stampId.trim()}
          onClick={() => stampExchange()}
          text="Trace exchange"
        />
        {stampedUntil ? (
          <span className="pb-2 text-xs text-green-700">
            <span className="font-mono">{stampedUntil.id}</span> traced until{' '}
            {ts(stampedUntil.until)}
          </span>
        ) : null}
      </div>

      <div className="mb-2 flex items-end gap-2">
        <div className="w-28">
          <Select
            name="trace-interval"
            label="Refresh"
            options={INTERVALS}
            value={intervalSec}
            onChange={(val: number) => setIntervalSec(val)}
          />
        </div>

        <Button
          color={ButtonColors.plain}
          disabled={!intervalSec}
          onClick={() => setPaused(p => !p)}
          text={paused ? 'Resume' : 'Pause'}
        />
        <Button
          color={ButtonColors.plain}
          onClick={() => {
            sinceIdRef.current = 0;
            setRows([]);
          }}
          text="Clear"
        />
        <span className="pb-2 text-xs text-gray-500">{rows.length} entries (newest first)</span>
        <div className="cursor-pointer mb-1.5" onClick={() => poll().catch(() => undefined)}>
          <Icon icon={SharedIcons.refresh} className="h-6 w-6" />
        </div>
      </div>

      {rows.length ? (
        <div className="[&_td]:!align-top [&_tbody_tr]:cursor-pointer">
          <DataTable
            columns={traceColumns}
            data={rows}
            size="compact"
            pagination={{ pageSize: 50, pageSizeOptions: [50, 100] }}
            onRowClick={r => setSelected(r)}
          />
        </div>
      ) : (
        <p className="rounded border p-3 text-xs text-gray-500">
          No trace entries yet — start a session, then exercise the pipeline (run the credential
          check, or stage/validate a key).
        </p>
      )}
      {selected ? (
        <ActivityDetailModal
          row={selected}
          onClose={() => setSelected(null)}
          onReviewed={onReviewed}
        />
      ) : null}
    </section>
  );
};

export default OpsConsole;
